import { cn } from './cn'

export interface TrendChartPoint {
  label: string
  value: number
}

export interface TrendChartProps {
  data?: TrendChartPoint[]
  /** Y-axis max; defaults to the max value (clamped to >= 1). */
  max?: number
  /** Accessible summary; also the visible empty/screen-reader fallback. */
  ariaLabel?: string
  unit?: string
  empty?: boolean
  emptyLabel?: string
  loading?: boolean
  /**
   * CRM-LOWMED-009: show the visible x-axis (point) labels, y-axis max/zero ticks,
   * and a legend so the chart is readable WITHOUT hovering. Defaults to true; pass
   * `false` for a compact sparkline. Per-bar value tooltips + the aria summary are
   * always present regardless.
   */
  showLabels?: boolean
  /** Legend text for the series (e.g. "Leads"). Shown when `showLabels`. */
  seriesLabel?: string
  className?: string
}

/** A readable axis label: pass a YYYY-MM-DD through as `MMM D`, else verbatim. */
function axisLabel(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  if (!m) return raw
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[Number(m[2]) - 1] ?? m[2]
  return `${month} ${Number(m[3])}`
}

/**
 * House-built inline-SVG time-series bar trend (no chart dependency). Uses the
 * canonical VJAL data-viz tokens (chart-3 series, chart-1 peak, border-line
 * gridlines). Renders an honest empty card for `data === []` rather than a
 * zero-value chart that implies data.
 *
 * CRM-LOWMED-009 (dashboard chart had poor/missing labeling): the chart now
 * carries VISIBLE x-axis date labels, a y-axis max/zero scale, per-bar count
 * values, per-bar hover tooltips (<title>), and a legend — so the "New leads by
 * day" widget is readable and accessible at a glance, not just on hover.
 */
export function TrendChart({
  data = [],
  max,
  ariaLabel,
  unit = '',
  empty,
  emptyLabel = 'No data recorded yet',
  loading = false,
  showLabels = true,
  seriesLabel = 'Count',
  className = ''
}: TrendChartProps) {
  const isEmpty = empty ?? data.length === 0
  if (loading) {
    return <EmptyChart className={className} label="Loading…" role="status" />
  }
  if (isEmpty) {
    return <EmptyChart className={className} label={emptyLabel} />
  }

  const ceiling = Math.max(1, max ?? Math.max(...data.map((point) => point.value)))
  const width = 320
  const height = 120
  const gap = 6
  // Cap each bar's slot so a handful of points cannot stretch into giant,
  // context-free slabs (CRM report 2026-08-13): bars keep a readable proportion
  // at every container width, and the figure centers in the leftover space.
  const maxSlot = 56
  const slot = Math.min(maxSlot, (width - gap * (data.length - 1)) / data.length)
  const contentWidth = slot * data.length + gap * (data.length - 1)
  const offsetX = (width - contentWidth) / 2
  const summary =
    ariaLabel ?? `Trend over ${data.length} points; latest ${data[data.length - 1]?.value}${unit}.`
  // Thin out x-axis labels when crowded so they stay legible (show ~7 evenly).
  const labelStride = Math.max(1, Math.ceil(data.length / 7))
  const gridlines = [0.25, 0.5, 0.75]

  return (
    <figure className={cn('m-0', className)}>
      {showLabels && (
        <figcaption className="mb-2 flex items-center gap-2 text-xs text-ink-muted">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-chart-3" aria-hidden="true" />
          <span>{seriesLabel}</span>
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-chart-1 ml-2" aria-hidden="true" />
          <span>Most recent</span>
        </figcaption>
      )}
      <div className={cn('flex', showLabels && 'gap-2')}>
        {/* Y-axis scale (max / zero) so bar heights are interpretable. */}
        {showLabels && (
          <div
            className="flex w-6 shrink-0 flex-col justify-between py-0.5 text-right text-chart-label leading-none text-ink-muted tabular-nums"
            aria-hidden="true"
          >
            <span>{ceiling}</span>
            <span>0</span>
          </div>
        )}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="h-32 w-full"
          preserveAspectRatio="none"
          role="img"
          aria-label={summary}
        >
          {/* Quarter gridlines give the stretched bars a readable scale. */}
          {gridlines.map((fraction) => (
            <line
              key={fraction}
              x1="0"
              y1={height - 1 - (height - 8) * fraction}
              x2={width}
              y2={height - 1 - (height - 8) * fraction}
              className="stroke-line"
              strokeWidth="1"
              strokeDasharray="3 4"
              aria-hidden="true"
            />
          ))}
          <line x1="0" y1={height - 1} x2={width} y2={height - 1} className="stroke-line" strokeWidth="1" />
          {data.map((point, index) => {
            const barHeight = Math.max(1, (point.value / ceiling) * (height - 8))
            const x = offsetX + index * (slot + gap)
            const peak = index === data.length - 1
            return (
              <rect
                key={point.label}
                x={x}
                y={height - barHeight - 1}
                width={slot}
                height={barHeight}
                rx="2"
                className={peak ? 'fill-chart-1' : 'fill-chart-3'}
              >
                <title>{`${axisLabel(point.label)}: ${point.value}${unit}`}</title>
              </rect>
            )
          })}
        </svg>
      </div>
      {/* Visible per-point VALUE row + x-axis DATE labels (CRM-LOWMED-009). Slot-
          centered under each bar so values stay attached to their bars at any
          container width (was flex-1 spread to the container edges). */}
      {showLabels && (
        <div className="mt-1 flex gap-2">
          <div className="w-6 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div
              className="flex justify-between text-chart-label font-medium leading-none text-ink-body tabular-nums"
              style={{ paddingLeft: `${(offsetX / width) * 100}%`, paddingRight: `${(offsetX / width) * 100}%` }}
              aria-hidden="true"
            >
              {data.map((point, index) => (
                <span key={`v:${point.label}`} className={cn('flex-1 text-center', index === data.length - 1 && 'text-vjal-700')}>
                  {point.value}
                </span>
              ))}
            </div>
            <div
              className="mt-0.5 flex justify-between text-chart-label leading-tight text-ink-muted"
              style={{ paddingLeft: `${(offsetX / width) * 100}%`, paddingRight: `${(offsetX / width) * 100}%` }}
              aria-hidden="true"
            >
              {data.map((point, index) => (
                <span key={`x:${point.label}`} className="flex-1 truncate text-center">
                  {index % labelStride === 0 || index === data.length - 1 ? axisLabel(point.label) : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      <span className="sr-only">{summary}</span>
    </figure>
  )
}

function EmptyChart({ className = '', label, role }: { className?: string; label: string; role?: string }) {
  return (
    <div
      className={cn('grid h-32 w-full place-items-center rounded-control border border-dashed border-line bg-whisper', className)}
      role={role}
    >
      <span className="px-4 text-center text-xs leading-5 text-ink-muted">{label}</span>
    </div>
  )
}
